import React, { ReactElement, useMemo } from 'react';
import { MapContainer, Marker, Tooltip, TileLayer } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import L, { LatLngTuple } from 'leaflet';
import { MapState } from '../reducer';
import { PortAndWaterwayObject, UseParams } from '../../../../types';
import { useNavigate, useParams } from 'react-router-dom';
import { BUOY_TYPES, COORD_PATTERN, NODE_ID_PATTERN } from '../../../../utils';
import MarkerImage from './marker-image';
import ErrorMessageBoundary from '../../../../errors/error-message-boundary';
import SxkMapContainer from './sxk-map-container';
import { navigateAction } from '../../router/actions';

const defaultCenter: LatLngTuple = [60.84491057364915, 18.8525390625];
const defaultZoom = 5;

const getIcon = (zoom: number, iconUrl: string, iconColor: string, iconBorderColor: string = '#0755e4') => {
  if (zoom > 10) {
    return L.divIcon({
      html: `<div class="pin-icon" style="background-image: url('${iconUrl}'); background-color: ${iconColor}; border: 2px solid ${iconBorderColor};" />`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
    // return L.icon({ iconUrl: iconUrl, iconSize: [28, 42], iconAnchor: [14, 42] });
  } else {
    return L.divIcon({
      html: '<div class="pin-icon" style="background-color: ' + iconColor + '; border: 2px solid ' + iconBorderColor + ';" />',
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });
  }
};

const isSelectedCategory = (selectedCategories: string[], type: string) => {
  if (!selectedCategories) {
    return true;
  }
  return selectedCategories.includes(type);
};

export default function SxkMap(): ReactElement {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug }: UseParams = useParams();
  const { objects, selectedCategories } = useSelector((state: { map: MapState }) => ({
    objects: state.map.objects,
    configuration: state.map.configuration,
    selectedCategories: state.map.selectedCategories,
  }));
  const [latitude, longitude, zoom] = (slug && slug.split(',')) || [];
  const zoomNumber = (zoom && parseInt(zoom, 10)) || defaultZoom;
  const validSlug = COORD_PATTERN.test(slug);
  const center = (validSlug && ([+latitude, +longitude] as LatLngTuple)) || defaultCenter;
  const markers = useMemo(
    () =>
      objects
        .filter((o) => isSelectedCategory(selectedCategories, o.type))
        .filter((pin, index, self) => self.findIndex((o) => o.id === pin.id) === index)
        .map((pin: PortAndWaterwayObject) => (
          <Marker
            key={pin.id}
            position={[pin.latitude, pin.longitude]}
            icon={getIcon(zoomNumber, pin.iconUrl, pin.iconColor, pin.iconBorderColor)}
            eventHandlers={{
              click: () => {
                if (NODE_ID_PATTERN.test(pin.id)) {
                  location.href = `/node/${pin.id.substring(1)}`;
                } else {
                  const path = `/sjokort/${pin.id}`;
                  dispatch(navigateAction(path));
                  navigate(path);
                }
              },
            }}
          >
            <Tooltip>
              {pin.image && (
                <div>
                  <MarkerImage alt={pin.name} src={pin.image} />
                </div>
              )}
              <h3>{pin.name}</h3>
              {pin.boatGateCode || pin.chartNumber ? (
                <dl>
                  <dt>Båtportskod</dt>
                  <dd>{pin.boatGateCode || '-'}</dd>
                  <dt>Sjökortsnummer</dt>
                  <dd>{pin.chartNumber || '-'}</dd>
                </dl>
              ) : null}
              <div className="icon-logo">{pin.position}</div>
            </Tooltip>
          </Marker>
        )),
    [objects, navigate, selectedCategories, zoomNumber, dispatch]
  );

  return (
    <ErrorMessageBoundary name="sxk-map" errorIsClearable>
      <MapContainer id="sxk-map-root" center={center} maxZoom={16} zoom={zoomNumber}>
        <SxkMapContainer>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <TileLayer url="https://t1.openseamap.org/seamark/{z}/{x}/{y}.png" />
          {markers}
        </SxkMapContainer>
      </MapContainer>
    </ErrorMessageBoundary>
  );
}
